'use client';

import { c as _c } from "react/compiler-runtime";
import './recent-activity-panel-transactions.scss';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { CommitmentAvatarGroup } from '../../molecules/commitment-avatar-group/commitment-avatar-group.tsx';
import { type RecentActivityPanelTransactionRow, getRecentActivityPanelTransactionRows, useComponentRecentActivityPanelTransactions } from './recent-activity-panel-transactions.logic.ts';
const PanelWrapper: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="RecentActivityPanelTransactions @container rounded-md flex flex-col">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PanelTitleWrapper: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex items-center justify-between px-comfy-lg py-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PanelRowWrapper: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="RecentActivityPanelTransactions__row flex items-center gap-comfy-lg">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PanelFooterWrapper: React.FC<React.PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="RecentActivityPanelTransactions__footer flex flex-col px-comfy-lg py-comfy-sm gap-snug-xl">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * A single row in the panel. Renders avatar(s) resolved from the row's
 * commitments, the completed date, an optional payment-issue badge, and the
 * row total.
 */
const RecentActivityPanelTransactionsRow: React.FC<{
  row: RecentActivityPanelTransactionRow;
}> = t0 => {
  const $ = _c(28);
  const {
    row
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    commitments
  } = row;
  let t2;
  if ($[1] !== commitments) {
    t2 = <div className="RecentActivityPanelTransactions__avatar shrink-0"><CommitmentAvatarGroup dynamicSize={true} commitments={commitments} layout="stacked" /></div>;
    $[1] = commitments;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== row.requestedAt || $[4] !== t) {
    t3 = t("my:recentActivity.transactions.transactionDate", {
      date: row.requestedAt
    });
    $[3] = row.requestedAt;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="label-mild label-md label-neutral-mild">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== row.hasIssue || $[9] !== row.hasPaymentIssue || $[10] !== t) {
    t5 = row.hasIssue && <Badge size="sm" color={row.hasPaymentIssue ? "negative" : "neutral"} label={t(row.hasPaymentIssue ? "my:recentActivity.transactions.paymentIssueBadge" : "my:recentActivity.transactions.issueBadge")} />;
    $[8] = row.hasIssue;
    $[9] = row.hasPaymentIssue;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex-1 flex flex-col gap-snug-md items-start">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = !row.hasPaymentIssue;
  let t8;
  if ($[15] !== row.hasPaymentIssue || $[16] !== t7) {
    t8 = cn("label-mild label-lg", {
      "label-negative-moderate": row.hasPaymentIssue,
      "label-neutral-mild": t7
    });
    $[15] = row.hasPaymentIssue;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== row.total || $[19] !== t) {
    t9 = t("core:numbers.currencyCustom", {
      amount: row.total,
      useNegativeParens: true
    });
    $[18] = row.total;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="text-right"><span className={t8}>{t9}</span></div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t2 || $[26] !== t6) {
    t11 = <PanelRowWrapper>{t2}{t6}{t10}</PanelRowWrapper>;
    $[24] = t10;
    $[25] = t2;
    $[26] = t6;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
const RecentActivityPanelTransactionsSkeletonRow: React.FC = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="RecentActivityPanelTransactions__avatar shrink-0"><Skeleton circle={true} width={36} height={36} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex-1"><Skeleton width={95} height={28} /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PanelRowWrapper>{t0}{t1}<div className="shrink-0 text-right"><Skeleton width={51} height={28} /></div></PanelRowWrapper>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

/**
 * Loading placeholder for `<RecentActivityPanelTransactions />`. Mirrors the panel's
 * structure (header, rows, footer) so the layout stays stable across states.
 */
export const RecentActivityPanelTransactionsSkeleton: React.FC<{
  /** Number of skeleton rows to render. Defaults to 3. */
  rowCount?: number;
}> = t0 => {
  const $ = _c(6);
  const {
    rowCount: t1
  } = t0;
  const rowCount = t1 === undefined ? 3 : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PanelTitleWrapper><Skeleton width={200} height={28} /></PanelTitleWrapper>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== rowCount) {
    t3 = <div className="px-comfy-lg overflow-hidden"><div className="flex flex-col">{Array.from({
          length: rowCount
        }, _temp)}</div></div>;
    $[1] = rowCount;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <PanelFooterWrapper><Skeleton height={11} /><Skeleton height={11} /><Skeleton width="40%" height={11} /></PanelFooterWrapper>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <PanelWrapper>{t2}{t3}{t4}</PanelWrapper>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
export const RecentActivityPanelTransactions: React.FC = () => {
  const $ = _c(27);
  const {
    transactions,
    commitments,
    isLoading
  } = useComponentRecentActivityPanelTransactions();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[1] !== commitments || $[2] !== isLoading || $[3] !== t || $[4] !== transactions) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const rows = getRecentActivityPanelTransactionRows(transactions, commitments);
      if (isLoading) {
        let t5;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <RecentActivityPanelTransactionsSkeleton />;
          $[10] = t5;
        } else {
          t5 = $[10];
        }
        t4 = t5;
        break bb0;
      }
      if (!rows.length) {
        t4 = null;
        break bb0;
      }
      T0 = PanelWrapper;
      let t5;
      if ($[11] !== t) {
        t5 = t("my:recentActivity.transactions.title");
        $[11] = t;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      if ($[13] !== t5) {
        t3 = <PanelTitleWrapper><h2 className="label-potent label-lg label-neutral-amplified">{t5}</h2></PanelTitleWrapper>;
        $[13] = t5;
        $[14] = t3;
      } else {
        t3 = $[14];
      }
      t1 = "flex flex-col px-comfy-lg overflow-hidden";
      t2 = rows.map(_temp2);
    }
    $[1] = commitments;
    $[2] = isLoading;
    $[3] = t;
    $[4] = transactions;
    $[5] = T0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    T0 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[15] !== t1 || $[16] !== t2) {
    t5 = <div className={t1}>{t2}</div>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t) {
    t6 = t("my:recentActivity.transactions.help");
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <PanelFooterWrapper><span className="label-md label-moderate label-neutral-mild">{t6}</span></PanelFooterWrapper>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== T0 || $[23] !== t3 || $[24] !== t5 || $[25] !== t7) {
    t8 = <T0>{t3}{t5}{t7}</T0>;
    $[22] = T0;
    $[23] = t3;
    $[24] = t5;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
function _temp(_, index) {
  return <RecentActivityPanelTransactionsSkeletonRow key={`skeleton-${index}`} />;
}
function _temp2(row) {
  return <RecentActivityPanelTransactionsRow key={row.id} row={row} />;
}